import type { FreeTextStaticContent } from '@core-api/editor/types/response';
import { MentiError } from '@mentimeter/error-utils/error';
import { captureException } from '@mentimeter/errors/sentry';
import type { Question } from '@mentimeter/legacy-series-types/question';
import { type CompatibilitySlide } from '../../compatibility-types';
import { isContentType } from '../../utils/is-content-type';
import { toSlug } from '../../utils/to-slug';
import { instructionsBarVisibilityToQuestionHideInstructions } from '../converters/slide/instructions-visibility-to-question-hide-instructions';
import { qrCodeVisibilityToQuestionHideJoinInstructions } from '../converters/slide/qrcode-visibility-to-question-hide-join-instructions';
import { visibilityToQuestionSkipSlide } from '../converters/slide/visibility-to-question-skip-slide';
import {
  staticContentTypeToModuleId,
  staticContentTypeToQuestionType,
  staticContentTypeToSlideType,
} from '../converters/static-content';
import { UncommonPropertyError } from '../errors/uncommon-property-error';
import { getCachedThemeSettingsProperty } from './utils/get-cached-theme-settings-property';

export const COMMON_QUESTION_PROPERTIES = [
  'admin_key',
  'hide_join_instructions',
  'hide_voting_instructions',
  'id',
  'module_id',
  'override_theme',
  'public_key',
  'question_description_styled',
  'skip_slide',
  'slide_type',
  'slug',
  'speaker_notes',
  'theme_settings',
  'title_meta',
  'type',
] as const satisfies Array<keyof Question>;

export type CommonQuestionProperty =
  (typeof COMMON_QUESTION_PROPERTIES)[number];

export const isCommonQuestionProperty = (
  prop: keyof Question,
): prop is CommonQuestionProperty => {
  return COMMON_QUESTION_PROPERTIES.includes(prop as CommonQuestionProperty);
};

export const getCommonQuestionProperty = (
  slide: CompatibilitySlide,
  prop: CommonQuestionProperty,
) => {
  if (!isCommonQuestionProperty(prop)) {
    throw new UncommonPropertyError(prop);
  }

  const staticContents = slide.staticContent;

  if (staticContents) {
    switch (prop) {
      case 'admin_key':
        return slide.slideAdminKey;
      case 'public_key':
        return slide.slidePublicKey;
      case 'hide_join_instructions':
        return qrCodeVisibilityToQuestionHideJoinInstructions(
          slide.qrCodeVisibility,
        );
      case 'hide_voting_instructions':
        return instructionsBarVisibilityToQuestionHideInstructions(
          slide.instructionsBarVisibility,
        );
      case 'id':
        return slide.slideAdminKey;
      case 'override_theme': {
        return slide.design.colors.custom;
      }
      case 'theme_settings': {
        return getCachedThemeSettingsProperty(slide);
      }
      case 'title_meta':
        // @ts-expect-error [MX] MX-634
        return staticContents.label ?? '';
      case 'skip_slide':
        return visibilityToQuestionSkipSlide(slide.visibility);
      case 'question_description_styled':
        return isContentType<FreeTextStaticContent>(
          slide.staticContent,
          'free-text',
        )
          ? (slide.staticContent.styledBody ?? {}) // this is used only for free-text slide
          : {};
      case 'module_id': {
        return staticContentTypeToModuleId(staticContents.type);
      }
      case 'type': {
        return staticContentTypeToQuestionType(staticContents.type);
      }
      case 'slide_type': {
        return staticContentTypeToSlideType(staticContents.type);
      }
      case 'speaker_notes': {
        return slide.speakerNotes;
      }
      case 'slug': {
        return toSlug(slide.title);
      }

      default:
        const error = new MentiError(
          `Unhandled common question property: \`${String(prop)}\``,
          {
            feature: 'compatibility-layer',
          },
        );
        captureException(error);

        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.warn(error.message);
        }

        return slide[prop];
    }
  }
};
